<template>
  <div>
    <div
      @click="handleToggleAIAssistant"
      class="ai-assistant-btn"
      :class="{ 'ai-assistant-btn--active': isPanelOpen }"
    >
      <div class="d-flex align-center ga-2">
        <coro-icon :icon-name="CoroIcons.AI_ASSISTANT" class="ai-assistant-icon" />
        <span class="subtitle1 white-space-nowrap">{{ $t("aiAssistant.title") }}</span>
      </div>
    </div>

    <v-card v-if="isPanelOpen" class="ai-assistant-sidebar position-fixed" width="400">
      <ai-assistant-panel :context="context" @close="handleClosePanel" />
    </v-card>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, onMounted, watch } from "vue";
import { storeToRefs } from "pinia";
import { useAiAssistantStore } from "@/_store/ai-assistant.module.ts";
import { useWorkspaceStore } from "@/_store/workspace.module.ts";
import { useSidebarPanelStore, SidebarId } from "@/_store/sidebar-panel.module.ts";
import { GtmAction, GTMParams } from "@/constants/gtm.ts";
import { trackGtmEvent } from "@/_helpers/utils.ts";
import { CoroIcons } from "@/constants/coro-icon.ts";
import AiAssistantPanel from "@/components/ai-assistant/AiAssistantPanel.vue";
import CoroIcon from "@/components/CoroIcon.vue";
import { useAppsManagementModule } from "@/_store/apps-mgmt.module.ts";

export default defineComponent({
  components: {
    AiAssistantPanel,
    CoroIcon,
  },
  setup() {
    const aiAssistantStore = useAiAssistantStore();
    const workspaceStore = useWorkspaceStore();
    const appsManagementModule = useAppsManagementModule();
    const sidebarPanelStore = useSidebarPanelStore();
    const { workspace } = storeToRefs(workspaceStore);
    const { context } = storeToRefs(appsManagementModule);
    const isPanelOpen = computed(() => sidebarPanelStore.isOpen(SidebarId.AI_ASSISTANT));

    const handleToggleAIAssistant = () => {
      trackGtmEvent(GtmAction.APP_BAR, GTMParams.AI_ASSISTANT_BUTTON_CLICKED);
      sidebarPanelStore.toggleSidebar(SidebarId.AI_ASSISTANT);
    };

    const handleClosePanel = () => {
      sidebarPanelStore.closeSidebar();
    };

    // Reset the assistant when switching workspace or MSP contexts so we don't reuse stale sessions
    watch(
      [() => workspace.value.workspaceId, context],
      ([newWorkspaceId, newContext], [oldWorkspaceId, oldContext]) => {
        const workspaceChanged =
          oldWorkspaceId && newWorkspaceId && newWorkspaceId !== oldWorkspaceId;

        const contextChanged = oldContext && newContext && newContext !== oldContext;

        // If either has changed, run the reset logic
        if (workspaceChanged || contextChanged) {
          sidebarPanelStore.closeSidebar();
          aiAssistantStore.$reset();
        }
      }
    );

    onMounted(async () => {
      await aiAssistantStore.ensureActiveSession();
    });

    return {
      isPanelOpen,
      handleToggleAIAssistant,
      handleClosePanel,
      CoroIcons,
      context,
    };
  },
});
</script>

<style lang="scss" scoped>
.ai-assistant-btn {
  height: 40px;
  border-radius: 8px;
  background: linear-gradient(90deg, rgba(205, 141, 229, 0.15) 0%, rgba(22, 164, 246, 0.15) 100%);
  text-transform: none;
  letter-spacing: normal;
  padding: 0 16px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.3s ease;

  i.icon:before {
    color: rgb(var(--v-theme-primary)) !important;
  }

  &:hover,
  &.ai-assistant-btn--active {
    background-color: rgb(var(--v-theme-primary));
    .subtitle1 {
      color: rgb(var(--v-theme-white)) !important;
    }
  }
}

.ai-assistant-sidebar {
  top: 90px;
  right: 24px;
  height: calc(100vh - 114px);
  z-index: 1000;
  box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.12) !important;
}

.ai-assistant-icon {
  width: 24px;
  height: 24px;
}
</style>
